import { usersApi, type UserEntity, type CreateUserRequest, type UpdateUserRequest } from '../../api/UsersApi';
import { shortUrlApi } from '../../api/ShortUrlApi';

/** Where a reset code is redeemed; kept in sync with the route in AppRouter. */
export const RESET_PASSWORD_PATH = '/reset-password';

export type UserRecord = {
  id: string;
  name: string;
  email: string;
  username: string;
  role: string;
  status: string;
  title: string;
  description: string;
  service: string;
  occupation: string;
  phoneNo: string;
  country: string;
  address: string;
  profilePicture: string;
  profilePictureUrl: string;
  isProfileDisplay: boolean;
  performService: boolean;
  createdAt: string;
};

export type FetchResult = {
  records: UserRecord[];
  total: number;
  skip: number;
  limit: number;
};

class UsersStore {
  private toRecord(entity: UserEntity): UserRecord {
    return {
      id: entity.userId,
      name: entity.name,
      email: entity.email || entity.username,
      username: entity.username,
      role: entity.role,
      status: entity.status,
      title: entity.title || '',
      description: entity.description || '',
      service: entity.service || '',
      occupation: entity.occupation || '',
      phoneNo: entity.phoneNo || '',
      country: entity.country || '',
      address: entity.address || '',
      profilePicture: entity.profilePicture || '',
      profilePictureUrl: entity.profilePictureFile?.s3Url || '',
      isProfileDisplay: entity.isProfileDisplay ?? false,
      performService: entity.performService ?? true,
      createdAt: entity.createdAt,
    };
  }

  async fetch(params?: { skip?: number; limit?: number; search?: string }): Promise<FetchResult> {
    const { data: response } = await usersApi.list(params);
    const list = Array.isArray(response.data) ? response.data : [];

    return {
      records: list.map((e) => this.toRecord(e)),
      total: response.total ?? list.length,
      skip: response.skip ?? 0,
      limit: response.limit ?? 10,
    };
  }

  async create(input: CreateUserRequest): Promise<UserRecord> {
    const { data } = await usersApi.create(input);
    return this.toRecord(data);
  }

  async update(id: string, input: UpdateUserRequest): Promise<UserRecord> {
    const { data } = await usersApi.update(id, input);
    return this.toRecord(data);
  }

  async delete(id: string): Promise<void> {
    await usersApi.delete(id);
  }

  /**
   * Issues a fresh reset link for the user and returns the URL to hand over.
   * Any link generated earlier stops working — the service reuses the row and
   * rolls a new code. Returns null when the service answers without one.
   */
  async generateResetPasswordLink(id: string): Promise<string | null> {
    const { data } = await shortUrlApi.resetPassword(id);
    const code = data?.data?.code;
    return code ? `${window.location.origin}${RESET_PASSWORD_PATH}/${code}` : null;
  }
}

export const usersStore = new UsersStore();
